import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useApi from "../../../../../utils/api";
import { getToastErrorMessage } from "../../../../../utils/helpers/errors";
import { useVisible } from "../../../../../utils/hooks/useVisible";
import { useDeleteModal } from "../../../helpers/useDeleteModal";
import { DeleteAllocationModal } from "./DeleteAllocationModal";

export const useWorkingShiftDelete = ({ remove, batchRemove, permission }) => {
	const { t } = useTranslation();
	const { authGet } = useApi();
	const { visible, selected, open, close } = useVisible();

	const removeWorkingShifts = useCallback(
		(data, params) => {
			const ids = Array.isArray(data) ? data : [data];
			return ids.length > 1
				? batchRemove(ids, undefined, undefined, params)
				: remove(ids[0], undefined, undefined, params);
		},
		[remove, batchRemove],
	);

	const handleConfirm = useCallback(
		async (data) => {
			const ids = Array.isArray(data) ? data : [data];
			try {
				const previews = await Promise.all(
					ids.map((id) => authGet(`/working-shift/${id}/delete-preview`)),
				);
				const shiftPremiums = previews.flatMap((preview) => preview?.shiftPremiums || []);
				const allocationChanges = previews.flatMap(
					(preview) => preview?.allocationChanges || [],
				);

				if (shiftPremiums.length) {
					toast.error(
						t("working-shift-delete-blocked-by-shift-premiums", {
							rules: shiftPremiums.map((rule) => rule.code).filter(Boolean).join(", "),
						}),
					);
					return;
				}

				if (allocationChanges.length) {
					open({ ids, allocationChanges });
					return;
				}

				await removeWorkingShifts(ids);
			} catch (err) {
				toast.error(getToastErrorMessage(err));
			}
		},
		[authGet, removeWorkingShifts, open, t],
	);

	const { modal: deleteModal, open: openDeleteModal } = useDeleteModal({
		message: (data) =>
			Array.isArray(data)
				? t("do-you-want-to-delete-working-shifts", { count: data?.length })
				: t("do-you-want-to-delete-working-shift"),
		onConfirm: handleConfirm,
		permission,
	});

	const confirmAllocationDelete = useCallback(
		() => removeWorkingShifts(selected?.ids, { confirm: true }),
		[removeWorkingShifts, selected],
	);

	const modals = (
		<>
			{deleteModal}

			<DeleteAllocationModal
				isOpen={visible}
				close={close}
				allocationChanges={selected?.allocationChanges}
				onConfirm={confirmAllocationDelete}
			/>
		</>
	);

	return [openDeleteModal, modals];
};
